import { Check, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Floating sticky bulk-action bar for the task inbox.
 *
 * Slides in from the top of the table panel when at least one row is
 * selected; collapses when selection drops to zero. v1 has a single
 * action (`Mark done`); future actions add buttons in the same right-
 * side cluster.
 *
 * The "select all on page" tri-state checkbox lives in `TaskListHeader`
 * (the toolbar above this bar), not here — putting it in both places
 * meant the user saw two near-identical checkboxes once a row was
 * selected. Toolbar wins because it's discoverable BEFORE any row is
 * checked; the action bar focuses on the count chip + actions.
 *
 * Sits below the impersonation banner (sticky `top-0` inside the
 * scrolling table panel, not inside the page layout) so the
 * impersonation context stays visible above it.
 */
interface TaskBulkActionBarProps {
  /** Number of currently selected rows (drives "X selected" copy). */
  count: number;
  /** Fire `Mark done` on the selected ids. */
  onMarkDone: () => void;
  /** Clear selection (✕ button). Also bound to Escape by the parent. */
  onClear: () => void;
  /** Disables the action button while the mutation is in flight. */
  isPending?: boolean;
}

export function TaskBulkActionBar({
  count,
  onMarkDone,
  onClear,
  isPending,
}: TaskBulkActionBarProps): React.ReactElement | null {
  if (count === 0) return null;
  return (
    <div
      role="region"
      aria-label="Bulk actions"
      className="sticky top-0 z-20 flex items-center justify-between gap-3 border-b bg-background/95 px-4 py-2 text-sm shadow-sm backdrop-blur"
    >
      {/* Selection chip — count + clear (X) as one clickable element.
          Click anywhere on the chip clears the selection. Visual
          grouping makes the X unambiguous: it belongs to "1 selected",
          and removing the chip removes the selection. Tooltip surfaces
          the keyboard shortcut. */}
      <button
        type="button"
        onClick={onClear}
        aria-label={`Clear selection — ${count} selected`}
        title="Clear selection (Esc)"
        className="group/chip inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-2.5 py-0.5 text-sm font-medium text-foreground transition-colors hover:border-primary/50 hover:bg-primary/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
      >
        <span>{count} selected</span>
        <X className="h-3 w-3 text-muted-foreground transition-colors group-hover/chip:text-foreground" />
      </button>

      <div className="flex items-center gap-2">
        <Button size="sm" className="h-8 gap-1.5" onClick={onMarkDone} disabled={isPending}>
          {isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Check className="h-3.5 w-3.5" />
          )}
          Mark done
        </Button>
      </div>
    </div>
  );
}
